import { useState, useEffect } from "react";
import { X, IndianRupee, CheckCircle2, Tag, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { errorMessage, money } from "../../lib/format";

export default function UpdateLeadOrderPriceModal({ lead, order, isOpen, onClose, onUpdated }) {
  const [unitPrice, setUnitPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (order && isOpen) {
      setUnitPrice(order.unit_price > 0 ? String(order.unit_price) : "");
      setNotes(order.pricing_notes || "");
    }
  }, [order, isOpen]);

  if (!isOpen || !lead || !order) return null;

  const qty = parseFloat(order.quantity || 1) || 1;
  const parsedPrice = parseFloat(unitPrice) || 0;
  const totalValue = qty * parsedPrice;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        unit_price: parsedPrice,
        notes: notes.trim(),
      };

      await api("patch", `/leads/${lead.lead_id}/orders/${order.order_id}/price`, payload);
      toast.success(
        parsedPrice > 0
          ? `Price set to ${money(parsedPrice)}/unit for ${order.product}`
          : `Price cleared for ${order.product}`
      );

      if (onUpdated) onUpdated();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to update order price"));
    } finally {
      setSubmitting(false);
    }
  };

  const isExistingPrice = order.unit_price && order.unit_price > 0;

  return (
    <div
      className="modal-backdrop"
      style={{
        zIndex: 10000,
        backgroundColor: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(4px)",
      }}
      data-testid="update-lead-order-price-modal-backdrop"
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 480,
          width: "95%",
          padding: "24px 26px",
          borderRadius: 14,
          boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(15, 23, 42, 0.06)",
        }}
        data-testid="update-lead-order-price-modal"
      >
        {/* Header */}
        <div className="modal-head" style={{ marginBottom: 16, alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                border: "1px solid #bfdbfe",
                color: "#2563eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 6px rgba(37, 99, 235, 0.12)",
                flexShrink: 0,
              }}
            >
              <Tag size={20} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: "#2563eb",
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                  marginBottom: 2,
                }}
              >
                LEAD ORDER PRICING
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#0f172a",
                  letterSpacing: "-0.3px",
                }}
              >
                {isExistingPrice ? "Update Order Price" : "Add Order Price"}
              </h2>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                Order: <b style={{ color: "#0f172a", fontFamily: "monospace" }}>{order.order_id}</b> · {lead.company_name}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            data-testid="close-update-price-modal"
            style={{
              padding: 6,
              borderRadius: 6,
              color: "#64748b",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              cursor: "pointer",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Order Details Pill */}
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 12.5,
          }}
        >
          <div>
            <span style={{ color: "#64748b" }}>Product:</span>{" "}
            <strong style={{ color: "#0f172a" }}>{order.product}</strong>
          </div>
          <div style={{ color: "#64748b" }}>
            Qty: <strong style={{ color: "#0f172a" }}>{qty.toLocaleString()} {order.unit || "pcs"}</strong>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} data-testid="lead-order-price-form">
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
            {/* Unit Price Input */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "#1e293b",
                  marginBottom: 6,
                }}
              >
                Unit Price (₹ / {order.unit || "unit"})
              </label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  border: "1.5px solid #cbd5e1",
                  borderRadius: 8,
                  backgroundColor: "#ffffff",
                  overflow: "hidden",
                }}
              >
                <span
                  style={{
                    padding: "0 12px",
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f8fafc",
                    borderRight: "1.5px solid #cbd5e1",
                    color: "#64748b",
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  ₹
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00 (Enter price received)"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                  autoFocus
                  data-testid="lead-order-update-price-input"
                  style={{
                    width: "100%",
                    height: 40,
                    border: "none",
                    outline: "none",
                    padding: "8px 12px",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#0f172a",
                    background: "transparent",
                  }}
                />
              </div>
            </div>

            {/* Total Calculation Card */}
            {parsedPrice > 0 && (
              <div
                style={{
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: 8,
                  padding: "10px 14px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <span style={{ fontSize: 11.5, color: "#1e40af", display: "block" }}>
                    Calculated Total ({qty} {order.unit || "pcs"} × {money(parsedPrice)})
                  </span>
                  <strong style={{ fontSize: 16, color: "#1d4ed8", fontWeight: 700 }}>
                    {money(totalValue)}
                  </strong>
                </div>
                <div style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "#ffffff", padding: "3px 8px", borderRadius: 4, border: "1px solid #bfdbfe" }}>
                  Excl. GST
                </div>
              </div>
            )}

            {/* Pricing Notes / Vendor Source */}
            <div>
              <label
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "#1e293b",
                  marginBottom: 6,
                }}
              >
                <span>Pricing Notes / Vendor Source</span>
                <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>Optional</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Quoted by Om Packaging on 01/10"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="lead-order-price-notes-input"
                style={{
                  width: "100%",
                  height: 38,
                  fontSize: 13,
                  border: "1.5px solid #cbd5e1",
                  borderRadius: 8,
                  padding: "8px 12px",
                }}
              />
            </div>
          </div>

          {/* Modal Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 10,
              paddingTop: 14,
              borderTop: "1px solid #e2e8f0",
            }}
          >
            <button
              type="button"
              className="outline-btn"
              onClick={onClose}
              disabled={submitting}
              data-testid="cancel-update-price-btn"
              style={{
                height: 36,
                fontSize: 13,
                fontWeight: 600,
                padding: "0 16px",
                borderRadius: 8,
                border: "1.5px solid #cbd5e1",
                background: "#ffffff",
                color: "#475569",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              disabled={submitting}
              data-testid="save-lead-order-price-btn"
              style={{
                height: 36,
                fontSize: 13,
                fontWeight: 600,
                padding: "0 18px",
                borderRadius: 8,
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                border: "none",
                color: "#ffffff",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                boxShadow: "0 2px 8px rgba(37, 99, 235, 0.25)",
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              <CheckCircle2 size={15} />
              {submitting ? "Saving Price..." : isExistingPrice ? "Update Price" : "Add Price"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
